import React, {useContext, useState} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {Link} from "react-router-dom";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowLeft01Icon, ArrowRight01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {SignInContext} from "../../../context/SignInContext";
import {OpenbrainLogoLink} from "../../../components/links/OpenbrainLogoLink";
import {FitrLogoLink} from "../../../components/links/FitrLogoLink";
import {c, mono, softShadow, primaryButtonSx} from "../../landing/landingTokens";
import {LiquidEther} from "../../../components/voice-orb";

const fieldSx = {
  "& .MuiFilledInput-root": {
    borderRadius: "12px",
    backgroundColor: "rgba(20,21,26,0.04)",
    overflow: "hidden",
    transition: "background-color .2s ease, box-shadow .2s ease",
    "&:before, &:after": {display: "none"},
    "&:hover": {backgroundColor: "rgba(20,21,26,0.06)"},
    "&.Mui-focused": {
      backgroundColor: "rgba(129,140,248,0.08)",
      boxShadow: "0 0 0 2px rgba(129,140,248,0.45)",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {color: c.purpleDeep},
};

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const {onForgotPassword} = useContext(SignInContext);
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const loginHandle = async (e) => {
    e.preventDefault();

    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      toast.error("Please enter email address");
      return;
    }

    const toastId = toast.loading("Sending request for forgot password");
    try {
      setLoading(true);
      await onForgotPassword({email: normalizedEmail});
      toast.update(toastId, {
        type: "success",
        render: "Please check your email address",
        isLoading: false,
        autoClose: 2000,
      });
      setEmail("");
    } catch (err) {
      toast.update(toastId, {
        type: "error",
        render: err?.message || "Please try again later.",
        isLoading: false,
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      position: "relative",
      width: "100%",
      minHeight: "100%",
      bgcolor: c.bg,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>
      <Box aria-hidden sx={{position: "absolute", inset: 0, zIndex: 0, opacity: 0.3, pointerEvents: "none"}}>
        <LiquidEther
          colors={["#8B7CFF", "#E2A6FF", "#C7D2FE"]}
          mouseForce={12}
          cursorSize={90}
          resolution={0.5}
          autoDemo={!reduceMotion}
          autoSpeed={0.3}
          autoIntensity={1.0}
          autoResumeDelay={800}
          autoRampDuration={1}
        />
      </Box>

      <Stack sx={{position: "relative", zIndex: 1, flex: 1, minHeight: "100%"}}>
        <Toolbar sx={{gap: 1.75}}>
          <img src="/logo.svg" alt="logo" width={30}/>
          <Box
            component={Link}
            to="/"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontFamily: mono,
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: c.purpleDeep,
              textDecoration: "none",
              opacity: 0.9,
              "&:hover": {opacity: 1},
            }}
          >
            <HugeiconsIcon icon={ArrowLeft01Icon} size={10}/> openbrain.io
          </Box>
        </Toolbar>

        <Box sx={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: 2, py: 3}}>
          <Stack
            direction="column"
            gap={3}
            sx={{
              maxWidth: 420,
              width: "100%",
              boxSizing: "border-box",
              px: {xs: 3, sm: 4.5},
              py: {xs: 4, sm: 5},
              borderRadius: "22px",
              bgcolor: c.white,
              boxShadow: softShadow,
              mx: "auto",
            }}
            justifyContent="center"
            alignItems="center"
          >
            <Stack direction="column" gap={1.25} sx={{width: "100%"}}>
              <Typography sx={{
                fontFamily: mono,
                fontSize: 11.5,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: c.purpleDeep,
                textAlign: "center",
              }}>
                OpenBrain
              </Typography>
              <Typography sx={{
                fontSize: {xs: 34, sm: 42},
                fontWeight: 700,
                letterSpacing: "-0.03em",
                color: c.ink,
                textAlign: "center",
                lineHeight: 1.05,
              }}>
                Forgot password?
              </Typography>
              <Box sx={{textAlign: "center"}}>
                <Typography sx={{color: c.inkSoft, fontSize: 14.5}} component="span">
                  Remember your password?
                </Typography>
                <Link to="/login" style={{
                  paddingLeft: 5,
                  color: c.purpleDeep,
                  fontWeight: 600,
                  textDecoration: "none",
                }}>
                  Sign in
                </Link>
              </Box>
            </Stack>

            <Stack component="form" onSubmit={loginHandle} direction="column" gap={1.5} sx={{width: "100%"}}>
              <TextField
                variant="filled"
                required
                fullWidth
                type="email"
                label="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter your email address"
                sx={fieldSx}
              />
              <Button
                fullWidth
                type="submit"
                disabled={loading || !email.trim()}
                sx={{...primaryButtonSx, py: 1.4, fontSize: 15}}
                endIcon={loading
                  ? <CircularProgress size={16} color="inherit"/>
                  : <HugeiconsIcon icon={ArrowRight01Icon} size={13}/>}>
                Send reset link
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Toolbar sx={{justifyContent: "space-between"}}>
          <OpenbrainLogoLink/>
          <FitrLogoLink/>
        </Toolbar>
      </Stack>
    </Box>
  );
};
